import { type FC, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { ClipboardCheckFill, ClipboardFill, EyeFill } from 'react-bootstrap-icons';
import { useNotifications } from '../../../modules/hooks';

interface AccessCodeModalProps {
  show: boolean;
  handleClose: () => void;
  accessCode: string;
  errorMessage?: string | null;
}

export const AccessCodeModal: FC<AccessCodeModalProps> = ({
  show,
  handleClose,
  accessCode,
  errorMessage,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasError = !!errorMessage;
  const hasAccessCode = !!accessCode;
  const canCopyCode = hasAccessCode && !hasError;
  const canToggleVisibility = hasAccessCode && !hasError;

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [copied]);

  useEffect(() => {
    setCopied(false);
  }, [accessCode, show]);

  const copyToClipboardHandler = () => {
    if (!canCopyCode) return;
    navigator.clipboard.writeText(accessCode);
    setSimpleToasts({
      type: 'success',
      message: 'Access code copied successfully',
      show: true,
    });
    setCopied(true);
  };

  const ClipboardIcon = copied ? ClipboardCheckFill : ClipboardFill;

  const inputType = hasError ? 'text' : showPassword ? 'text' : 'password';
  const inputValue = hasError ? (errorMessage ?? '') : accessCode;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="post-editor-modal modal-200w"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title className="editor-title">Access Code</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col xs={12}>
              <Form.Label className="editor-title-label"> Your Access Code</Form.Label>
            </Col>
            <Col xs={10}>
              <Form.Group>
                <Form.Control
                  readOnly
                  type={inputType}
                  value={inputValue}
                  isInvalid={hasError}
                />
              </Form.Group>
            </Col>
            <Col xs={2}>
              <Button
                disabled={!canToggleVisibility}
                onClick={() => setShowPassword(!showPassword)}
              >
                <EyeFill size={18} />
              </Button>
            </Col>
          </Row>
          {hasError && errorMessage && (
            <Row>
              <Col xs={12}>
                <Form.Text className="text-danger">{errorMessage}</Form.Text>
              </Col>
            </Row>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer
        style={{
          display: 'flex',
          justifyContent: 'end',
          marginTop: '24px',
        }}
      >
        <Button className="cancel-button" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="save-button"
          onClick={() => copyToClipboardHandler()}
          disabled={!canCopyCode}
        >
          <ClipboardIcon size={18} style={{ marginRight: '8px' }} />
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
